import React, { useCallback, useEffect } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import Animated, {
    SharedValue,
    interpolate,
    interpolateColor,
    useAnimatedStyle,
    useSharedValue,
    withSpring,
} from 'react-native-reanimated';

import { ds } from '@/components/lib/design.system';
import { useCurrencyOverviewModal } from '@/feature/account-details-sweeps/components/CurrencyOverviewModalProvider';
import { useSwitchActiveWallet } from '@/hooks/use-active-wallet';
import { useCurrencyGuidance } from '@/hooks/use-currency-guidance';
import { Product } from '@/hooks/use-jurisdiction';
import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';
import { designSystem } from '@/styles/styles';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';
import { create } from 'zustand';

import { SocialCurrencyActiveIcon } from './SocialCurrencyActiveIcon';

type AnimationStore = {
    sharedAnimation: SharedValue<0 | 1> | undefined;
    actions: {
        setSharedAnimation: (animation: SharedValue<0 | 1>) => void;
    };
};

export const useAnimationStore = create<AnimationStore>()(set => {
    return {
        sharedAnimation: undefined,
        actions: {
            setSharedAnimation: (sharedAnimation: SharedValue<0 | 1>) => set({ sharedAnimation }),
        },
    };
});

const CURRENCY_POSITION = {
    CASH: 1,
    COINS: 0,
} as const;

const SPRING_CONFIG = {
    mass: 0.8,
    damping: 50,
    stiffness: 150,
    overshootClamping: false,
    restSpeedThreshold: 0.001,
    restDisplacementThreshold: 0.001,
} as const;

const ANIMATION_RANGE = {
    START: 2,
    END: 30,
} as const;

const TOGGLE_BORDER_RADIUS = 100;

export const SocialCurrencyToggle = ({ disabled }: { disabled?: boolean }) => {
    const switchActiveWallet = useSwitchActiveWallet();
    const activeCurrency = useSocialCurrencyStore(state => state.activeCurrency);
    const setActiveCurrency = useSocialCurrencyStore(state => state.actions.setActiveCurrency);
    const sharedAnimation = useAnimationStore(state => state.sharedAnimation);
    const { showCurrencyOverviewModal } = useCurrencyOverviewModal();
    const { shouldShow, markAsShown } = useCurrencyGuidance();
    let progress = useSharedValue(
        activeCurrency === SocialCurrency.CASH ? CURRENCY_POSITION.CASH : CURRENCY_POSITION.COINS
    );
    const setSharedAnimation = useAnimationStore(state => state.actions.setSharedAnimation);
    // Shared animation here is used to sync the animation between screens
    progress = sharedAnimation ?? progress;

    useEffect(() => {
        if (!sharedAnimation) {
            setSharedAnimation(progress);
        }
    }, [setSharedAnimation, progress, sharedAnimation]);

    useEffect(() => {
        progress.value = withSpring(
            activeCurrency === SocialCurrency.CASH ? CURRENCY_POSITION.CASH : CURRENCY_POSITION.COINS,
            SPRING_CONFIG
        );
    }, [activeCurrency, progress]);

    const onPress = useCallback(() => {
        triggerHapticFeedback();
        const newCurrency: SocialCurrency =
            activeCurrency === SocialCurrency.CASH ? SocialCurrency.COINS : SocialCurrency.CASH;
        setActiveCurrency(newCurrency);
        switchActiveWallet(Product.Sweeps);
        progress.value = withSpring(
            newCurrency === SocialCurrency.CASH ? CURRENCY_POSITION.CASH : CURRENCY_POSITION.COINS,
            SPRING_CONFIG
        );

        // Currency Guidance Modal -- only appear if never shown before
        if (shouldShow) {
            markAsShown();
            showCurrencyOverviewModal({
                isTouchpoint: false,
                currency: newCurrency,
            });
        }
    }, [
        activeCurrency,
        setActiveCurrency,
        switchActiveWallet,
        progress,
        shouldShow,
        markAsShown,
        showCurrencyOverviewModal,
    ]);

    const animatedStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(
            progress.value,
            [CURRENCY_POSITION.COINS, CURRENCY_POSITION.CASH],
            [ds.colorValue.background.accent.amber.strong, ds.colorValue.background.success.strong]
        ),
    }));

    const translateStyle = useAnimatedStyle(() => ({
        transform: [
            {
                translateX: interpolate(
                    progress.value,
                    [CURRENCY_POSITION.COINS, CURRENCY_POSITION.CASH],
                    [ANIMATION_RANGE.START, ANIMATION_RANGE.END]
                ),
            },
        ],
    }));

    return (
        <>
            <Pressable onPress={onPress} disabled={disabled} style={styles.pressable}>
                <Animated.View style={[styles.containerStyle, animatedStyle]}>
                    <Animated.View style={[styles.circleStyle, translateStyle]}>
                        <SocialCurrencyActiveIcon />
                    </Animated.View>
                </Animated.View>
            </Pressable>
        </>
    );
};

const styles = StyleSheet.create({
    circleStyle: {
        width: 32,
        height: 32,
        backgroundColor: ds.colorValue.background.neutral.base,
        borderRadius: 32,
        justifyContent: 'center',
        alignItems: 'center',
        shadowOffset: {
            width: 0,
            height: 1,
        },
        shadowColor: designSystem.colors.neutral900,
        shadowOpacity: 0.25,
        shadowRadius: 2,
        elevation: 1,
    },
    containerStyle: {
        width: 64,
        height: 36,
        justifyContent: 'center',
        borderRadius: TOGGLE_BORDER_RADIUS,
    },
    pressable: {
        borderRadius: TOGGLE_BORDER_RADIUS,
    },
});
